import { Injectable } from "@angular/core";
import { API } from "aws-amplify";
import { AppBanner, AppNotification, NotificationType, SalesMailSettingType, SalesMailSettings } from "./shared-models/settings.model";
import { NewRecordValue } from "./shared-models/record-metadata";
import { BehaviorSubject, Observable } from "rxjs";
import { UsersService } from "./users/users.service";


export interface SettingsArrayResponse<T> {
  message: string;
  value: T[];
}

export interface SettingsValueResponse<T> {
  message: string;
  value: T;
}

@Injectable({
  providedIn: 'root'
})
export class PortalSettingsService {

  readonly apiName = "SalesMailUser";
  readonly apiVersion = "2016-04-18";
  readonly awsRegion = "us-east-1";

  private _ActiveBannerSubject: BehaviorSubject<AppBanner|null> = new BehaviorSubject(null);
  public ActiveBanner: Observable<AppBanner> = this._ActiveBannerSubject.asObservable();

  private _ActiveNotificationsSubject: BehaviorSubject<AppNotification[]|null> = new BehaviorSubject(null);
  public ActiveNotifications: Observable<AppNotification[]> = this._ActiveNotificationsSubject.asObservable();


  constructor(private user_service: UsersService) {
    this.loadData();
   }

  async loadData() {
    try {
      const portalSettings = await this.getActivePortalSettings();
      const bannerSettings = portalSettings.value.filter(s => s.type == 'Portal' && s.name == 'BANNER_ID') || [];
      const notificationSettings = portalSettings.value.filter(s => s.type =='Portal' && s.name == 'NOTIFICATION_ID');

      if(bannerSettings && bannerSettings.length > 0) {
        // Only should ever be one banner max, take first record
        const bannerResult = await this.getBannerForSetting(bannerSettings[0]);        
        const banner = bannerResult.value || null;
        this._ActiveBannerSubject.next(banner);
      } else {
        this._ActiveBannerSubject.next(null);
      }

      if(notificationSettings && notificationSettings.length > 0) {
        // Can have multiple notifications
        const notificationResult = await this.getNotificationsForSettings(notificationSettings);        
        const notifications = notificationResult.value || null;
        this._ActiveNotificationsSubject.next(notifications);        
      } else {
        this._ActiveNotificationsSubject.next(null);
      }

    } catch (error) {
      //console.error("Error loading settings:", error);
    }    
  }

  public getClassStringForNotificationType(type: NotificationType): string {
    switch (type) {
      case 'Info':
        return 'alert alert-primary'
        
      case 'Warning':
        return 'alert alert-warning'

      case 'Error':
        return 'alert alert-error';
    }
  }

  public async getActivePortalSettings(): Promise<SettingsArrayResponse<SalesMailSettings>> {
    const type: SalesMailSettingType = 'Portal';
    let path = `/app-settings?type=${type}`;
    return API.get(this.apiName, path, {});
  }

  public async getBannerForSetting(setting: SalesMailSettings): Promise<SettingsValueResponse<AppBanner>> {
    if(setting.type != "Portal" || setting.name != 'BANNER_ID') {
      return;
    }

    let path = `/app-banners?id=${setting.value}`;
    return API.get(this.apiName, path, {});
  }

  public async getNotificationsForSettings(settings: SalesMailSettings[]): Promise<SettingsArrayResponse<AppNotification>> {
    //Get a commma separted string of ids for all portal settings that contain notification ids
    const ids = (settings.filter(s => s.type == 'Portal' && s.name == 'NOTIFICATION_ID').map(s => s.value) || []).join(',');    
    let path = `/app-notifications`;
    const init = {
      queryStringParameters: {
        ids: ids
      }
    };
    return API.get(this.apiName, path, init);
  }

  public async recordBannerHit(banner_id: number): Promise<SettingsValueResponse<NewRecordValue>> {
    let path = `/app-banner-hits`;
    let init = {
      body: {
        app_banner_id: banner_id
      },
    };

    return API.post(this.apiName, path, init);
  } 

  //Used in last active reports. Calls api once per day
  public async ping(): Promise<any> {

    const ping_key = 'LAST_PING';
    const last_ping = localStorage.getItem(ping_key);
    const today = new Date().toISOString().split('T')[0]; // Current date as 'YYYY-MM-DD'

    if (last_ping !== today) {
      const path = `/user/ping`;

      try {
        const ids = await this.user_service.getCognitoUserIds();
        await API.post(this.apiName, path, {body: { cognitoid: ids.cognitoid}});        
        localStorage.setItem(ping_key, today);
      } catch (error) {
        console.error('Error sending activity ping', error);
      }            
    } 
  }

}
